// @ts-nocheck
import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.refs.details?.addEventListener("toggle", this.#onDetailsToggle);

    // Close on outside click
    document.addEventListener("click", this.#handleOutsideClick);

    // URL change detection (SPA-safe + hash)
    this.#setupUrlChangeListener();

    // Initial pass in case the page loads already on a “section URL”
    this.#handleUrlChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.refs.details?.removeEventListener("toggle", this.#onDetailsToggle);

    document.removeEventListener("click", this.#handleOutsideClick);

    // Remove URL listeners we added
    window.removeEventListener("app:navigation", this.#handleUrlChange);
    window.removeEventListener("popstate", this.#handleUrlChange);
    window.removeEventListener("hashchange", this.#handleUrlChange);
  }

  // ---------- Outside click ----------
  #handleOutsideClick = (event) => {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) this.close();
  };

  // ---------- URL change detection ----------
  #setupUrlChangeListener() {
    // Patch history once (global) and fan-out via a custom event
    if (!window.__headerDrawerHistoryPatched) {
      window.__headerDrawerHistoryPatched = true;
      const notify = () => window.dispatchEvent(new Event("app:navigation"));

      const _pushState = history.pushState;
      history.pushState = function (...args) {
        const r = _pushState.apply(this, args);
        notify();
        return r;
      };

      const _replaceState = history.replaceState;
      history.replaceState = function (...args) {
        const r = _replaceState.apply(this, args);
        notify();
        return r;
      };
    }

    // Listen to our synthetic event + native ones
    window.addEventListener("app:navigation", this.#handleUrlChange);
    window.addEventListener("popstate", this.#handleUrlChange);
    window.addEventListener("hashchange", this.#handleUrlChange);
  }

  #handleUrlChange = () => {
    // Close first, then scroll (after animation unlocks body scroll)
    this.#closeThen(() => this.#scrollToSectionByUrl(location.href));
  };

  // Close the drawer; if it was open, run `fn` after close animation completes
  #closeThen(fn) {
    if (!this.isOpen) {
      // Not open → scroll right away (double RAF ensures layout is settled)
      requestAnimationFrame(() => requestAnimationFrame(fn));
      return;
    }

    const details = this.refs.details;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      details.removeEventListener("animationend", onEnd, { once: true });
      // Double RAF to ensure styles/overflow unlocks have applied
      requestAnimationFrame(() => requestAnimationFrame(fn));
    };

    const onEnd = () => finish();

    // Fallback timeout in case animationend doesn’t fire
    const fallback = setTimeout(finish, 450);

    details.addEventListener(
      "animationend",
      () => {
        clearTimeout(fallback);
        onEnd();
      },
      { once: true }
    );

    this.close();
  }

  // ---------- Scrolling ----------
  #scrollToSectionByUrl(url) {
    let targetId = null;

    if (url.includes("our-story")) {
      targetId = "group-block-our-story-mobile"; // ID 1
    } else if (url.includes("blog")) {
      targetId = "group-block-blog-mobile"; // ID 2
    } else if (url.includes("materials-and-care")) {
      targetId = "group-block-materials-and-care-mobile"; // ID 3
    }

    // Also support hash-only links like #group-block-blog
    if (!targetId && location.hash) {
      const hashId = location.hash.replace(/^#/, "");
      if (hashId) targetId = hashId;
    }

    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    const offset = this.#getHeaderOffset() + 120;
    const top =
      target.getBoundingClientRect().top + window.pageYOffset - offset;

    // Make target focusable for a11y, but don't jump on focus
    const prevTabIndex = target.getAttribute("tabindex");
    target.setAttribute("tabindex", "-1");

    // Perform the scroll
    window.scrollTo({ top, behavior: "smooth" });

    // Focus after a tick so Screen Readers announce it
    setTimeout(() => {
      target.focus({ preventScroll: true });
      // Clean up tabindex if it wasn't there
      if (prevTabIndex === null) target.removeAttribute("tabindex");
    }, 50);
  }

  #getHeaderOffset() {
    // Try actual header height; fallback to 80
    const header = document.querySelector(".header");
    const h = header?.getBoundingClientRect().height || 80;
    // If the header is not sticky on mobile, you can return 0 instead.
    return h;
  }

  // ---------- Existing code (unchanged) ----------
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;
    this.#close(this.#getDetailsElement(event));
  };

  #onDetailsToggle = () => {
    requestAnimationFrame(() =>
      this.#resetAllHorizontalScroll(this.refs.details || this)
    );
  };

  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  #resetScrollOf(el) {
    if (!el) return;
    const prevBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = "auto";
    el.scrollLeft = 0;
    requestAnimationFrame(() => {
      el.style.scrollBehavior = prevBehavior || "";
    });
  }

  #resetAllHorizontalScroll(scope = this) {
    const root = scope instanceof Element ? scope : this;
    const wrappers = root.querySelectorAll("[data-horizontal-scroll]");
    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      this.#resetScrollOf(wrapper);
      const list = wrapper.querySelector(".menu-drawer__featured-content-list");
      if (list instanceof HTMLElement) this.#resetScrollOf(list);
    });
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest("details") ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");
    if (!summary) return;

    summary.setAttribute("aria-expanded", "true");

    requestAnimationFrame(() => {
      details.classList.add("menu-open");
      this.#resetAllHorizontalScroll(details);
      onAnimationEnd(details, () => this.#resetAllHorizontalScroll(details));
    });

    trapFocus(details);
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector("summary");
    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    onAnimationEnd(details, () => {
      reset(details);
      this.#resetAllHorizontalScroll(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]");
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  #setupAnimatedElementListeners() {
    const removeWillChangeOnAnimationEnd = (event) => {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener(
          "animationend",
          removeWillChangeOnAnimationEnd
        );

        const wrapper = target.closest("[data-horizontal-scroll]");
        if (wrapper instanceof HTMLElement) {
          requestAnimationFrame(() => this.#resetAllHorizontalScroll(wrapper));
        }
      }
    };

    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
